import type { ProspectResponse } from "../../../../../types/api";
import {
  conflictId,
  displayFieldValue,
  prospectName,
  type FieldChoices,
  type FieldConflict,
} from "./mergeFields";

interface ConflictStepProps {
  survivor: ProspectResponse;
  losers: ProspectResponse[];
  conflicts: FieldConflict[];
  choices: FieldChoices;
  onChoiceChange: (conflictKey: string, prospectId: string) => void;
}

/**
 * Step 2 — resolve field-by-field conflicts. Only fields where the selected
 * prospects DIFFER are shown; each is a choice among the candidate values,
 * defaulting to the survivor's — except where the survivor has nothing and
 * another profile does, which preselects that real value so a merge never
 * defaults to throwing data away. Clearing the field stays one click away.
 */
export const ConflictStep = ({
  survivor,
  losers,
  conflicts,
  choices,
  onChoiceChange,
}: ConflictStepProps) => {
  const nameById = new Map<string, string>(
    [survivor, ...losers].map((p) => [p.id, prospectName(p)])
  );

  if (conflicts.length === 0) {
    return (
      <div className="merge-modal__no-conflicts" data-testid="merge-no-conflicts">
        <p>
          No conflicting fields — these profiles agree on every field. All data
          will be combined onto {prospectName(survivor)}.
        </p>
      </div>
    );
  }

  return (
    <fieldset className="merge-modal__fieldset">
      <legend className="merge-modal__legend">Resolve conflicting fields</legend>
      <p className="merge-modal__hint">
        {conflicts.length} field{conflicts.length !== 1 ? "s" : ""} differ. Pick
        the value to keep for each — the surviving profile's value is selected by
        default, or the other profile's when the survivor has none.
      </p>
      <ul className="merge-modal__conflict-list">
        {conflicts.map((conflict) => {
          const key = conflictId(conflict);
          const selected = choices[key] ?? conflict.defaultProspectId;
          return (
            <li
              key={key}
              className="merge-modal__conflict-item"
              data-testid={`conflict-${key}`}
            >
              <div className="merge-modal__conflict-label">{conflict.label}</div>
              <div className="merge-modal__conflict-options">
                {conflict.candidates.map((candidate) => {
                  const isSurvivorValue = candidate.prospectId === survivor.id;
                  return (
                    <label
                      key={candidate.prospectId}
                      className="merge-modal__radio-label merge-modal__radio-label--inline"
                    >
                      <input
                        type="radio"
                        name={`conflict-${key}`}
                        value={candidate.prospectId}
                        checked={selected === candidate.prospectId}
                        onChange={() => onChoiceChange(key, candidate.prospectId)}
                        data-testid={`conflict-${key}-option-${candidate.prospectId}`}
                      />
                      <span className="merge-modal__conflict-value">
                        {displayFieldValue(conflict, candidate.value)}
                      </span>
                      <span className="merge-modal__conflict-source">
                        {isSurvivorValue
                          ? candidate.value === null
                            ? "Leave empty (survivor)"
                            : "Keep (survivor)"
                          : `From ${nameById.get(candidate.prospectId) ?? "other"}`}
                      </span>
                    </label>
                  );
                })}
              </div>
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
};
